import { forwardRef, useState } from 'react'
import { WarningCircleIcon } from '@phosphor-icons/react'
import { ScoopText, ScoopAccordion, ScoopColumn, ScoopButton } from '../../../../../../ui'
import { MarkdownSection } from '../MarkdownSection/MarkdownSection'
import { AskAboutButton } from '../AskAboutButton/AskAboutButton'
import { ProbeTable } from '../ProbeTable/ProbeTable'
import { ActionCandidateCard } from '../../../ActionCandidates/ActionCandidateCard'
import { PeerEvidence } from '../PeerEvidence/PeerEvidence'
import { SeverityTag } from '../../../SeverityTag/SeverityTag'
import { isKnownSeverity } from '../../../../utils/severity'
import styles from './EntityDetail.module.scss'

const INITIAL_FINDINGS_CAP = 5
const INITIAL_PROBE_CAP = 8

const normalizeFindingText = (finding) => {
    if (typeof finding === 'string') return finding
    if (!finding) return ''
    return (
        finding.message ||
        finding.summary ||
        finding.detail ||
        finding.description ||
        JSON.stringify(finding)
    )
}

const splitFinding = (finding) =>
    normalizeFindingText(finding)
        .replace(/\s+•\s+/g, '\n')
        .split('\n')
        .map((line) => line.replace(/^[•\-*]\s*/, '').trim())
        .filter(Boolean)

const getDedupedFindings = (findings = [], seen = new Set()) => {
    const deduped = []

    findings.forEach((finding) => {
        splitFinding(finding).forEach((line) => {
            const normalized = line.replace(/\s+/g, ' ').toLowerCase()
            if (!seen.has(normalized)) {
                seen.add(normalized)
                deduped.push(line)
            }
        })
    })

    return deduped
}

const textValue = (value) => {
    if (typeof value === 'string') return value.trim()
    if (Array.isArray(value)) return value.map(textValue).filter(Boolean).join('\n\n')
    return ''
}

const humanizeFacet = (value) => {
    if (!value) return ''
    return value
        .toString()
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\b\w/g, (char) => char.toUpperCase())
}

const getEntityAnalysis = (entity) => {
    const synthesisJson = entity?.synthesisJson || {}
    const candidates = [
        synthesisJson.narrative,
        synthesisJson.analysis,
        synthesisJson.investigation_narrative,
        entity?.narrative,
        entity?.analysis,
        entity?.synthesis,
        synthesisJson.summary,
        entity?.summary,
    ]

    return candidates.map(textValue).find(Boolean) || ''
}

const buildFindingGroups = (entity) => {
    const seen = new Set()
    const groups = []
    const findingsByContext = entity.findingsByContext || {}

    Object.keys(findingsByContext).forEach((contextName) => {
        const findings = getDedupedFindings(findingsByContext[contextName] || [], seen)
        if (findings.length > 0) {
            groups.push({ contextName, findings })
        }
    })

    if (groups.length === 0 && Array.isArray(entity.keyFindings)) {
        const findings = getDedupedFindings(entity.keyFindings, seen)
        if (findings.length > 0) {
            groups.push({ contextName: 'Key Findings', findings })
        }
    }

    return groups
}

const getProbeGroupLabel = (probe) =>
    humanizeFacet(probe.contextName || probe.findingType) || 'Evidence'

const groupProbes = (probes) =>
    probes.reduce((groups, probe) => {
        const label = getProbeGroupLabel(probe)
        const existing = groups.find((group) => group.label === label)
        if (existing) {
            existing.probes.push(probe)
        } else {
            groups.push({ label, probes: [probe] })
        }
        return groups
    }, [])

const getProbeSummaryText = (probes) => {
    const groupCount = new Set(probes.map(getProbeGroupLabel)).size
    const rows = probes.reduce((total, probe) => total + (Number(probe.rowCount) || 0), 0)
    const parts = []

    if (groupCount > 1) parts.push(`${groupCount} evidence groups`)
    if (rows > 0) parts.push(`${rows} result rows`)

    return parts.join(' · ')
}

const ProbeEvidenceSection = ({ probes, runPath }) => {
    const [showAll, setShowAll] = useState(probes.length <= INITIAL_PROBE_CAP)
    const visibleProbes = showAll ? probes : probes.slice(0, INITIAL_PROBE_CAP)
    const hiddenCount = probes.length - visibleProbes.length
    const groupedProbes = groupProbes(visibleProbes)
    const summaryText = getProbeSummaryText(probes)

    return (
        <div className={styles.probesSection}>
            <ScoopAccordion className={styles.probesAccordion}>
                <ScoopAccordion.Summary className={styles.probesSummary}>
                    <div className={styles.probesSummaryContent}>
                        <ScoopText className={styles.fieldLabel}>
                            Data Probes ({probes.length})
                        </ScoopText>
                        {summaryText && (
                            <ScoopText className={styles.probesSummaryMeta}>
                                {summaryText}
                            </ScoopText>
                        )}
                    </div>
                </ScoopAccordion.Summary>
                <ScoopAccordion.Details className={styles.probesDetails}>
                    {groupedProbes.map((group) => (
                        <div key={group.label} className={styles.probeGroup}>
                            <div className={styles.probeGroupHeader}>
                                <ScoopText className={styles.probeGroupTitle}>
                                    {group.label}
                                </ScoopText>
                                <ScoopText className={styles.probeGroupCount}>
                                    {group.probes.length}{' '}
                                    {group.probes.length === 1 ? 'probe' : 'probes'}
                                </ScoopText>
                            </div>
                            {group.probes.map((probe, index) => (
                                <ProbeTable
                                    key={
                                        probe.probeId
                                            ? `${probe.probeId}@${probe.probeOrdinal ?? 0}`
                                            : `${group.label}-${index}`
                                    }
                                    probe={probe}
                                    runPath={runPath}
                                />
                            ))}
                        </div>
                    ))}
                    {hiddenCount > 0 && (
                        <ScoopButton
                            className={styles.showAllProbesButton}
                            variant="outlineGray"
                            size="xs"
                            onClick={() => setShowAll(true)}
                        >
                            Show all {probes.length} probes
                        </ScoopButton>
                    )}
                </ScoopAccordion.Details>
            </ScoopAccordion>
        </div>
    )
}

/**
 * Humanize a diagnosis chain like "death_spiral_diagnosis → leading_indicators → ..."
 */
const humanizeDiagnosis = (diagnosis) => {
    if (!diagnosis) return null
    const parts = diagnosis.split(/\s*→\s*/)
    const segment = parts[0].trim()
    return segment.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

const FindingsGroup = ({ contextName, findings, entity, runPath, capFindings = true }) => {
    const [expanded, setExpanded] = useState(false)
    const needsCap = capFindings && findings.length > INITIAL_FINDINGS_CAP
    const visibleFindings =
        needsCap && !expanded ? findings.slice(0, INITIAL_FINDINGS_CAP) : findings

    return (
        <div className={styles.contextGroup}>
            <div className={styles.contextHeader}>
                <ScoopText className={styles.contextName}>{contextName}</ScoopText>
            </div>
            <ul className={styles.findingsList}>
                {visibleFindings.map((finding, idx) => {
                    const message = normalizeFindingText(finding)
                    return (
                        <li key={idx} className={styles.findingItem}>
                            <MarkdownSection content={message} />
                            <AskAboutButton
                                entity={`${entity.entityType || ''} ${entity.entityId}`}
                                context={contextName}
                                finding={message}
                                runPath={runPath}
                            />
                        </li>
                    )
                })}
            </ul>
            {needsCap && !expanded && (
                <button
                    className={styles.showMoreButton}
                    onClick={() => setExpanded(true)}
                    type="button"
                >
                    Show all {findings.length} findings
                </button>
            )}
        </div>
    )
}

export const EntityDetail = forwardRef(
    (
        {
            entity,
            probes = [],
            runPath,
            actionCandidates = [],
            onAcceptAction,
            onRejectAction,
            acceptingAction,
            rejectingAction,
            pendingFingerprint,
            variant = 'accordion',
        },
        ref
    ) => {
        const findingGroups = buildFindingGroups(entity)
        const humanDiagnosis = humanizeDiagnosis(entity.diagnosis)
        const entityDisplayId = entity.displayEntityId || entity.displayLabel || entity.entityId
        const capFindings = variant !== 'section'
        const analysis = getEntityAnalysis(entity)
        // Contracted deliverable element (zero-or-more per entity — audit F5).
        const questions = Array.isArray(entity.synthesisJson?.questions_to_investigate)
            ? entity.synthesisJson.questions_to_investigate.filter(
                  (question) => typeof question === 'string' && question.trim()
              )
            : []

        const header = (
            <div className={styles.entityHeader}>
                <ScoopText className={styles.entityName}>
                    {entity.entityType && (
                        <span className={styles.entityType}>{entity.entityType}: </span>
                    )}
                    {entityDisplayId}
                </ScoopText>
                {entity.investigationCount != null && (
                    <ScoopText className={styles.investigationCount}>
                        {entity.investigationCount} analyses
                    </ScoopText>
                )}
                {isKnownSeverity(entity.severity) && <SeverityTag severity={entity.severity} />}
                {entity.unprobed === true && (
                    // "Make silence loud" (audit F6) — as a warning (icon+text),
                    // not a second severity pill (design D2).
                    <span className={styles.notProbed}>
                        <WarningCircleIcon size={14} className={styles.notProbedIcon} />
                        Not probed
                    </span>
                )}
            </div>
        )

        const body = (
            <ScoopColumn className={styles.entityBody}>
                {humanDiagnosis && (
                    <div className={styles.diagnosisSection}>
                        <ScoopText className={styles.fieldLabel}>Status</ScoopText>
                        <ScoopText className={styles.diagnosisText}>{humanDiagnosis}</ScoopText>
                    </div>
                )}

                {analysis && (
                    <div className={styles.analysisSection}>
                        <ScoopText className={styles.fieldLabel}>Analysis</ScoopText>
                        <div className={styles.analysisBody}>
                            <MarkdownSection content={analysis} />
                        </div>
                    </div>
                )}

                {questions.length > 0 && (
                    <div className={styles.questionsSection}>
                        <ScoopText className={styles.fieldLabel}>
                            Questions to Investigate ({questions.length})
                        </ScoopText>
                        <ul className={styles.questionsList}>
                            {questions.map((question, idx) => (
                                <li key={idx} className={styles.questionItem}>
                                    <MarkdownSection content={question} />
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <PeerEvidence peerEvidence={entity.peerEvidence} />

                {findingGroups.length > 0 && (
                    <div className={styles.findingsSection}>
                        <ScoopText className={styles.fieldLabel}>
                            {findingGroups.length === 1 &&
                            findingGroups[0].contextName === 'Key Findings'
                                ? 'Key Findings'
                                : 'Findings by Context'}
                        </ScoopText>
                        {findingGroups.map(({ contextName, findings }) => (
                            <FindingsGroup
                                key={contextName}
                                contextName={contextName}
                                findings={findings}
                                entity={entity}
                                runPath={runPath}
                                capFindings={capFindings}
                            />
                        ))}
                    </div>
                )}

                {actionCandidates.length > 0 && (
                    <div className={styles.actionsSection}>
                        <ScoopText className={styles.fieldLabel}>
                            Recommended Actions ({actionCandidates.length})
                        </ScoopText>
                        <div className={styles.actionCards}>
                            {actionCandidates.map((candidate) => (
                                <ActionCandidateCard
                                    key={candidate.candidateFingerprint}
                                    candidate={candidate}
                                    compact
                                    onAccept={onAcceptAction}
                                    onReject={onRejectAction}
                                    accepting={acceptingAction}
                                    rejecting={rejectingAction}
                                    pendingFingerprint={pendingFingerprint}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {probes.length > 0 && <ProbeEvidenceSection probes={probes} runPath={runPath} />}
            </ScoopColumn>
        )

        if (variant === 'section') {
            // The masthead (EntityReportPage) owns the entity name, severity,
            // and counts — the section renders the body only (review D25).
            return (
                <div ref={ref} id={`entity-${entity.entityId}`} className={styles.entitySection}>
                    {body}
                </div>
            )
        }

        return (
            <div ref={ref} id={`entity-${entity.entityId}`}>
                <ScoopAccordion>
                    <ScoopAccordion.Summary>{header}</ScoopAccordion.Summary>
                    <ScoopAccordion.Details>{body}</ScoopAccordion.Details>
                </ScoopAccordion>
            </div>
        )
    }
)

EntityDetail.displayName = 'EntityDetail'
